import { createSignal, createEffect, createMemo, onCleanup, onMount, For, Index, Show, untrack } from 'solid-js';
import { createStore } from 'solid-js/store';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import SetupModeButton from '../components/SetupModeButton';
import StorageSelect from '../components/StorageSelect';
import CoordinateInput, { type Coordinate } from '../components/CoordinateInput';
import AreaInput from '../components/AreaInput';
import type { SetupModeBlockPosDto, SetupModeValueDto, SellbotZoneDto, SellbotZoneMergeConflictDto, SellbotZoneRequest } from '../api/generated/schemas';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import type { SellbotConfigDto } from '../api';
import { PLOT_ACL_ACTIONS } from '../models/PlotAclAction';
import { useInventoryApi, type StorageInfo } from '../services/inventoryApi';
import { STORAGE_ACTIONS } from '../services/resourceActions';
import styles from './SellbotConfig.module.css';
import { firstError } from '../api/envelope';
import { isFeatureEnabled } from '../services/featureFlags';

interface ChamberStats {
    chestCount: number;
    bounds: { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number } | null;
    lastScannedAt: string | null;
}

const LOG_LEVELS = ['Debug', 'Info', 'Warning', 'Error'] as const;
// Asks the backend for a freshly created chamber storage.
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// What the bot actually enforces when it opens a chest (InteractionReach.ServerCentreReach), measured
// against GrieferGames' NCP. Only used to warn while picking; nothing here changes what the bot does.
const SELLBOT_REACH = 5.0;

const ZONE_MODES = ['Single', 'Multi'] as const;
const MULTI_ZONE_FLAG = 'SELLBOT_MULTI_ZONE';
// The bot answers a record request within ~35s and announces nothing, so the page watches the zone's timestamp.
const DROP_PATH_POLL_MS = 2000;
const DROP_PATH_WAIT_MS = 40000;

type ZoneForm = Required<SellbotZoneRequest>;
type ZoneSetup = Omit<ZoneForm, 'id' | 'name'>;

const EMPTY_ZONE_SETUP: ZoneSetup = {
    sellAreaMinX: 0, sellAreaMinY: 0, sellAreaMinZ: 0,
    sellAreaMaxX: 0, sellAreaMaxY: 0, sellAreaMaxZ: 0,
    dropYaw: 0, dropPitch: 0,
    ankaufChestX: 0, ankaufChestY: 0, ankaufChestZ: 0,
    lampX: 0, lampY: 0, lampZ: 0,
    quoteLampX: 0, quoteLampY: 0, quoteLampZ: 0,
};
const ZONE_SETUP_KEYS = Object.keys(EMPTY_ZONE_SETUP) as (keyof ZoneSetup)[];
const pickZoneSetup = (source: ZoneSetup): ZoneSetup =>
    Object.fromEntries(ZONE_SETUP_KEYS.map(key => [key, source[key]])) as ZoneSetup;

interface CoordBinding {
    read: () => Coordinate;
    write: (position: Coordinate) => void;
}

interface NumberBinding {
    read: () => number;
    write: (value: number) => void;
}

interface ConfigForm {
    defaultLogLevel: string;
    zoneMode: string;
    zones: ZoneForm[];
    sellChamberStorageId: string;
    homeName: string;
    homeX: number; homeY: number; homeZ: number;
    dropYaw: number; dropPitch: number; dropSettleDelayMs: number;
    sellAreaMinX: number; sellAreaMinY: number; sellAreaMinZ: number;
    sellAreaMaxX: number; sellAreaMaxY: number; sellAreaMaxZ: number;
    chamberAreaMinX: number; chamberAreaMinY: number; chamberAreaMinZ: number;
    chamberAreaMaxX: number; chamberAreaMaxY: number; chamberAreaMaxZ: number;
    chamberIntruderAction: string;
    ankaufEnabled: boolean;
    ankaufChestX: number; ankaufChestY: number; ankaufChestZ: number;
    verificationChestX: number; verificationChestY: number; verificationChestZ: number;
    trashChestX: number; trashChestY: number; trashChestZ: number;
    lagerChestX: number; lagerChestY: number; lagerChestZ: number;
    lampX: number; lampY: number; lampZ: number;
    ankaufTrigger: string;
    ankaufMsgTriggerEnabled: boolean;
    ankaufMsgTriggerWord: string;
    ankaufDefaultDestination: string;
    autoEnablePlotAcl: boolean;
    scanChamberOnEnable: boolean;
    ankaufAcceptsCompressed: boolean;
    allowCompressedChamberRetrieval: boolean;
    listAsCompressed: boolean;
    quoteEnabled: boolean;
    quoteLampX: number; quoteLampY: number; quoteLampZ: number;
    quoteTrigger: string;
    priceQueryEnabled: boolean;
}

const ANKAUF_TRIGGERS = ['LampOn', 'LampOff'] as const;
const ANKAUF_DESTINATIONS = ['SellChamber', 'LagerChest'] as const;

const emptyForm: ConfigForm = {
    defaultLogLevel: 'Info', zoneMode: 'Single', zones: [], sellChamberStorageId: '', homeName: '',
    homeX: 0, homeY: 0, homeZ: 0, dropYaw: 0, dropPitch: 0, dropSettleDelayMs: 150,
    sellAreaMinX: 0, sellAreaMinY: 0, sellAreaMinZ: 0,
    sellAreaMaxX: 0, sellAreaMaxY: 0, sellAreaMaxZ: 0,
    chamberAreaMinX: 0, chamberAreaMinY: 0, chamberAreaMinZ: 0,
    chamberAreaMaxX: 0, chamberAreaMaxY: 0, chamberAreaMaxZ: 0,
    chamberIntruderAction: 'Kick',
    ankaufEnabled: false,
    ankaufChestX: 0, ankaufChestY: 0, ankaufChestZ: 0,
    verificationChestX: 0, verificationChestY: 0, verificationChestZ: 0,
    trashChestX: 0, trashChestY: 0, trashChestZ: 0,
    lagerChestX: 0, lagerChestY: 0, lagerChestZ: 0,
    lampX: 0, lampY: 0, lampZ: 0,
    ankaufTrigger: 'LampOn',
    ankaufMsgTriggerEnabled: false,
    ankaufMsgTriggerWord: 'ankauf',
    ankaufDefaultDestination: 'SellChamber',
    autoEnablePlotAcl: false,
    scanChamberOnEnable: true,
    ankaufAcceptsCompressed: false,
    allowCompressedChamberRetrieval: false,
    listAsCompressed: false,
    quoteEnabled: false,
    quoteLampX: 0, quoteLampY: 0, quoteLampZ: 0,
    quoteTrigger: 'LampOn',
    priceQueryEnabled: false,
};

export default function SellbotConfig() {
    const api = useApi();
    const inv = useInventoryApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const blocksUnit = () => t('SELLBOT_UNIT_BLOCKS');

    const [form, setForm] = createStore<ConfigForm>({ ...emptyForm });
    const [loading, setLoading] = createSignal(true);
    const [saving, setSaving] = createSignal(false);
    const [capturing, setCapturing] = createSignal(false);
    const [chamber, setChamber] = createSignal<ChamberStats | null>(null);
    const [savedChamber, setSavedChamber] = createSignal<{ id: string; name: string | null } | null>(null);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [savedZones, setSavedZones] = createSignal<SellbotZoneDto[]>([]);
    const [zoneMergeConflicts, setZoneMergeConflicts] = createSignal<SellbotZoneMergeConflictDto[]>([]);
    const [recordingZoneId, setRecordingZoneId] = createSignal<string | null>(null);
    const [savedFormJson, setSavedFormJson] = createSignal('');
    const formDirty = createMemo(() => JSON.stringify(form) !== savedFormJson());
    const chamberChoices = () => storages().filter(s => s.id === savedChamber()?.id
        ? savedChamber()?.name != null
        : s.myActions.includes(STORAGE_ACTIONS.ConfigWrite) && (!s.sellChamberBotId || s.sellChamberBotId === botId()));

    const applyLiveState = (c: SellbotConfigDto) => {
        const bounds = (c.chamberMinX != null && c.chamberMinY != null && c.chamberMinZ != null
            && c.chamberMaxX != null && c.chamberMaxY != null && c.chamberMaxZ != null)
            ? { minX: c.chamberMinX, minY: c.chamberMinY, minZ: c.chamberMinZ, maxX: c.chamberMaxX, maxY: c.chamberMaxY, maxZ: c.chamberMaxZ }
            : null;
        setChamber({ chestCount: c.chamberChestCount ?? 0, bounds, lastScannedAt: c.chamberLastScannedAt ?? null });
        setSavedZones(c.zones);
        setZoneMergeConflicts(c.zoneMergeConflicts);
    };

    const applyConfig = (c: SellbotConfigDto) => {
        setSavedChamber({ id: c.sellChamberStorageId, name: c.sellChamberStorageName ?? null });
        const zonesInOrder = [...c.zones].sort((first, second) => first.sortOrder - second.sortOrder);
        setForm({
            defaultLogLevel: c.defaultLogLevel,
            zoneMode: c.zoneMode,
            zones: zonesInOrder.map(zone => ({ id: zone.id, name: zone.name, ...pickZoneSetup(zone) })),
            sellChamberStorageId: c.sellChamberStorageName != null ? c.sellChamberStorageId : '',
            homeName: c.homeName,
            homeX: c.homeX, homeY: c.homeY, homeZ: c.homeZ,
            dropYaw: c.dropYaw, dropPitch: c.dropPitch, dropSettleDelayMs: c.dropSettleDelayMs ?? 150,
            sellAreaMinX: c.sellAreaMinX, sellAreaMinY: c.sellAreaMinY, sellAreaMinZ: c.sellAreaMinZ,
            sellAreaMaxX: c.sellAreaMaxX, sellAreaMaxY: c.sellAreaMaxY, sellAreaMaxZ: c.sellAreaMaxZ,
            chamberAreaMinX: c.chamberAreaMinX, chamberAreaMinY: c.chamberAreaMinY, chamberAreaMinZ: c.chamberAreaMinZ,
            chamberAreaMaxX: c.chamberAreaMaxX, chamberAreaMaxY: c.chamberAreaMaxY, chamberAreaMaxZ: c.chamberAreaMaxZ,
            chamberIntruderAction: c.chamberIntruderAction ?? 'Kick',
            ankaufEnabled: c.ankaufEnabled ?? false,
            ankaufChestX: c.ankaufChestX ?? 0, ankaufChestY: c.ankaufChestY ?? 0, ankaufChestZ: c.ankaufChestZ ?? 0,
            verificationChestX: c.verificationChestX ?? 0, verificationChestY: c.verificationChestY ?? 0, verificationChestZ: c.verificationChestZ ?? 0,
            trashChestX: c.trashChestX ?? 0, trashChestY: c.trashChestY ?? 0, trashChestZ: c.trashChestZ ?? 0,
            lagerChestX: c.lagerChestX ?? 0, lagerChestY: c.lagerChestY ?? 0, lagerChestZ: c.lagerChestZ ?? 0,
            lampX: c.lampX ?? 0, lampY: c.lampY ?? 0, lampZ: c.lampZ ?? 0,
            ankaufTrigger: c.ankaufTrigger ?? 'LampOn',
            ankaufMsgTriggerEnabled: c.ankaufMsgTriggerEnabled ?? false,
            ankaufMsgTriggerWord: c.ankaufMsgTriggerWord ?? 'ankauf',
            ankaufDefaultDestination: c.ankaufDefaultDestination ?? 'SellChamber',
            autoEnablePlotAcl: c.autoEnablePlotAcl ?? false,
            scanChamberOnEnable: c.scanChamberOnEnable ?? true,
            ankaufAcceptsCompressed: c.ankaufAcceptsCompressed ?? false,
            allowCompressedChamberRetrieval: c.allowCompressedChamberRetrieval ?? false,
            listAsCompressed: c.listAsCompressed ?? false,
            quoteEnabled: c.quoteEnabled ?? false,
            quoteLampX: c.quoteLampX ?? 0, quoteLampY: c.quoteLampY ?? 0, quoteLampZ: c.quoteLampZ ?? 0,
            quoteTrigger: c.quoteTrigger ?? 'LampOn',
            priceQueryEnabled: c.priceQueryEnabled ?? false,
        });
        setSavedFormJson(JSON.stringify(form));
        applyLiveState(c);
    };

    const load = async () => {
        const id = botId();
        if (!id) { setLoading(false); return; }
        const [res, storageList] = await Promise.all([api.sellbot.getConfig(id), inv.fetchStorages()]);
        setStorages(storageList);
        if (isOk(res.data)) applyConfig(res.data.value);
        else {
            setForm({ ...emptyForm, zones: [] });
            setSavedFormJson(JSON.stringify(form));
            setSavedChamber(null);
            setSavedZones([]);
            setZoneMergeConflicts([]);
        }
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        setLoading(true);
        untrack(() => { void load(); });
        void id;
    });

    const pollLiveState = async (): Promise<SellbotConfigDto | null> => {
        const id = botId();
        if (!id) return null;
        const res = await api.sellbot.getConfig(id);
        if (!isOk(res.data)) return null;
        applyLiveState(res.data.value);
        return res.data.value;
    };

    onMount(() => {
        const handle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void pollLiveState();
        }, 5000);
        onCleanup(() => clearInterval(handle));
    });

    const saveConfig = async () => {
        const id = botId();
        if (!id) return;
        setSaving(true);
        const res = await api.sellbot.upsertConfig(id, {
            defaultLogLevel: form.defaultLogLevel, homeName: form.homeName,
            zoneMode: form.zoneMode,
            zones: form.zones.map(zone => ({ ...zone, name: zone.name.trim() })),
            sellChamberStorageId: form.sellChamberStorageId || EMPTY_GUID,
            homeX: form.homeX, homeY: form.homeY, homeZ: form.homeZ,
            dropYaw: form.dropYaw, dropPitch: form.dropPitch, dropSettleDelayMs: form.dropSettleDelayMs,
            sellAreaMinX: form.sellAreaMinX, sellAreaMinY: form.sellAreaMinY, sellAreaMinZ: form.sellAreaMinZ,
            sellAreaMaxX: form.sellAreaMaxX, sellAreaMaxY: form.sellAreaMaxY, sellAreaMaxZ: form.sellAreaMaxZ,
            chamberAreaMinX: form.chamberAreaMinX, chamberAreaMinY: form.chamberAreaMinY, chamberAreaMinZ: form.chamberAreaMinZ,
            chamberAreaMaxX: form.chamberAreaMaxX, chamberAreaMaxY: form.chamberAreaMaxY, chamberAreaMaxZ: form.chamberAreaMaxZ,
            chamberIntruderAction: form.chamberIntruderAction,
            ankaufEnabled: form.ankaufEnabled,
            ankaufChestX: form.ankaufChestX, ankaufChestY: form.ankaufChestY, ankaufChestZ: form.ankaufChestZ,
            verificationChestX: form.verificationChestX, verificationChestY: form.verificationChestY, verificationChestZ: form.verificationChestZ,
            trashChestX: form.trashChestX, trashChestY: form.trashChestY, trashChestZ: form.trashChestZ,
            lagerChestX: form.lagerChestX, lagerChestY: form.lagerChestY, lagerChestZ: form.lagerChestZ,
            lampX: form.lampX, lampY: form.lampY, lampZ: form.lampZ,
            ankaufTrigger: form.ankaufTrigger,
            ankaufMsgTriggerEnabled: form.ankaufMsgTriggerEnabled,
            ankaufMsgTriggerWord: form.ankaufMsgTriggerWord.trim(),
            ankaufDefaultDestination: form.ankaufDefaultDestination,
            autoEnablePlotAcl: form.autoEnablePlotAcl,
            scanChamberOnEnable: form.scanChamberOnEnable,
            ankaufAcceptsCompressed: form.ankaufAcceptsCompressed,
            allowCompressedChamberRetrieval: form.allowCompressedChamberRetrieval,
            listAsCompressed: form.listAsCompressed,
            quoteEnabled: form.quoteEnabled,
            quoteLampX: form.quoteLampX, quoteLampY: form.quoteLampY, quoteLampZ: form.quoteLampZ,
            quoteTrigger: form.quoteTrigger,
            priceQueryEnabled: form.priceQueryEnabled,
        });
        if (isOk(res.data)) { applyConfig(res.data.value); toast?.info(t('SELLBOT_TOAST_SAVED')); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_SAVE_FAILED'));
        setSaving(false);
    };

    const rescan = async () => {
        const id = botId();
        if (!id) return;
        const res = await api.sellbot.rescan(id);
        if (isOk(res.data)) toast?.info(t('SELLBOT_TOAST_RESCAN_QUEUED'));
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_RESCAN_FAILED'));
    };

    const captureHome = async () => {
        const id = botId();
        if (!id || !form.homeName?.trim()) return;
        setCapturing(true);
        const res = await api.sellbot.captureHome(id, { homeName: form.homeName });
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_CAPTURE_FAILED'));
            setCapturing(false);
            return;
        }
        toast?.info(t('SELLBOT_TOAST_CAPTURING'));
        await new Promise((r) => setTimeout(r, 6000));
        const c = await pollLiveState();
        if (c) {
            setForm('homeX', c.homeX);
            setForm('homeY', c.homeY);
            setForm('homeZ', c.homeZ);
            toast?.info(t('SELLBOT_TOAST_CAPTURE_DONE', `${c.homeX.toFixed(2)}, ${c.homeY.toFixed(2)}, ${c.homeZ.toFixed(2)}`));
        }
        setCapturing(false);
    };

    const isMultiZone = () => form.zoneMode === 'Multi';
    const defaultZoneName = (zoneNumber: number) => t('SELLBOT_ZONE_DEFAULT_NAME', zoneNumber);
    const nextFreeZoneName = () => {
        const takenNames = new Set(form.zones.map(zone => zone.name.trim()));
        let zoneNumber = 1;
        while (takenNames.has(defaultZoneName(zoneNumber))) zoneNumber++;
        return defaultZoneName(zoneNumber);
    };

    // Seeding zone 1 from the flat setup keeps the switch itself from changing anything in game.
    const changeZoneMode = (zoneMode: string) => {
        if (zoneMode === 'Multi' && form.zones.length === 0)
            setForm('zones', [{ id: null, name: defaultZoneName(1), ...pickZoneSetup(form) }]);
        setForm('zoneMode', zoneMode);
    };

    const addZone = () => setForm('zones', zones => [...zones, { id: null, name: nextFreeZoneName(), ...EMPTY_ZONE_SETUP }]);

    const removeZone = async (zoneIndex: number) => {
        const zone = form.zones[zoneIndex];
        if (zone.id && !(await showConfirm(t('SELLBOT_ZONE_REMOVE_CONFIRM', zone.name), true))) return;
        setForm('zones', zones => zones.filter((_, index) => index !== zoneIndex));
    };

    const savedZone = (zoneId: string | null) => zoneId ? savedZones().find(zone => zone.id === zoneId) : undefined;
    const zonesWithoutDropPath = createMemo(() =>
        form.zones.filter(zone => !savedZone(zone.id)?.dropPathRecordedAt).map(zone => zone.name));
    const mergeConflictZoneName = (zoneId: string) => savedZone(zoneId)?.name ?? t('SELLBOT_ZONE_REMOVED');

    const recordDropPath = async (zoneId: string) => {
        const id = botId();
        if (!id) return;
        const previousRecordedAt = savedZone(zoneId)?.dropPathRecordedAt ?? null;
        setRecordingZoneId(zoneId);
        const res = await api.sellbot.recordZoneDropPath(id, zoneId);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_DROP_PATH_FAILED'));
            setRecordingZoneId(null);
            return;
        }
        toast?.info(t('SELLBOT_TOAST_DROP_PATH_RECORDING'));
        const deadline = Date.now() + DROP_PATH_WAIT_MS;
        while (Date.now() < deadline && botId() === id) {
            await new Promise((resolve) => setTimeout(resolve, DROP_PATH_POLL_MS));
            const recordedZone = (await pollLiveState())?.zones.find(zone => zone.id === zoneId);
            if (recordedZone?.dropPathRecordedAt && recordedZone.dropPathRecordedAt !== previousRecordedAt) {
                toast?.success(t('SELLBOT_TOAST_DROP_PATH_RECORDED', recordedZone.name));
                setRecordingZoneId(null);
                return;
            }
        }
        toast?.warning(t('SELLBOT_TOAST_DROP_PATH_TIMEOUT'));
        setRecordingZoneId(null);
    };

    // One label row per pickable group. The Goggles button hands back a value in the kind's shape and
    // the setter writes it into the same store the inputs use, so it saves like anything typed by hand.
    const pickLabel = (
        labelKey: string,
        fieldId: string,
        kind: 'BlockPos' | 'ChestRef' | 'LookAngles',
        currentValue: () => SetupModeValueDto | undefined,
        apply: (value: SetupModeValueDto) => void,
    ) => (
        <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
            <span class={styles.fieldLabel}><Text val={labelKey} /></span>
            <SetupModeButton
                fieldId={fieldId}
                kind={kind}
                label={t(labelKey)}
                allowedBots={botId() ? [botId()!] : []}
                currentValue={currentValue}
                anchor={homeAnchor()}
                reach={SELLBOT_REACH}
                onCaptured={apply}
            />
        </div>
    );

    // The chest fields are worked from the sellbot's home, so the mod can say when a pick is too far
    // from where the bot will actually be standing.
    const homeAnchor = (): SetupModeBlockPosDto | undefined => {
        if (!form.homeX && !form.homeY && !form.homeZ) return undefined;
        return { x: Math.floor(form.homeX), y: Math.floor(form.homeY), z: Math.floor(form.homeZ) };
    };

    const formCoords = (x: keyof ConfigForm, y: keyof ConfigForm, z: keyof ConfigForm): CoordBinding => ({
        read: () => ({ x: form[x] as number, y: form[y] as number, z: form[z] as number }),
        write: (position) => {
            setForm(x as never, position.x as never);
            setForm(y as never, position.y as never);
            setForm(z as never, position.z as never);
        },
    });

    const zoneCoords = (zoneIndex: number, x: keyof ZoneSetup, y: keyof ZoneSetup, z: keyof ZoneSetup): CoordBinding => ({
        read: () => {
            const zone = form.zones[zoneIndex];
            return { x: zone[x], y: zone[y], z: zone[z] };
        },
        write: (position) => setForm('zones', zoneIndex, { [x]: position.x, [y]: position.y, [z]: position.z } as Partial<ZoneSetup>),
    });

    const formNumber = (key: keyof ConfigForm): NumberBinding => ({
        read: () => form[key] as number,
        write: (value) => setForm(key as never, value as never),
    });

    const zoneNumber = (zoneIndex: number, key: keyof ZoneSetup): NumberBinding => ({
        read: () => form.zones[zoneIndex][key],
        write: (value) => setForm('zones', zoneIndex, { [key]: value } as Partial<ZoneSetup>),
    });

    const isUnset = (position: Coordinate) => !position.x && !position.y && !position.z;

    const blockValue = (coords: CoordBinding) => (): SetupModeValueDto | undefined => {
        const position = coords.read();
        if (isUnset(position)) return undefined;
        return { kind: 'BlockPos', blockPos: position };
    };

    const chestValue = (coords: CoordBinding) => (): SetupModeValueDto | undefined => {
        const position = coords.read();
        if (isUnset(position)) return undefined;
        return { kind: 'ChestRef', chest: { ...position, type: 'SingleChest' } };
    };

    const lookValue = (yaw: NumberBinding, pitch: NumberBinding) => (): SetupModeValueDto | undefined =>
        ({ kind: 'LookAngles', lookAngles: { yaw: yaw.read(), pitch: pitch.read() } });

    const applyBlock = (coords: CoordBinding) => (value: SetupModeValueDto) => {
        if (!value.blockPos) return;
        coords.write(value.blockPos);
    };

    const applyChest = (coords: CoordBinding) => (value: SetupModeValueDto) => {
        if (!value.chest) return;
        coords.write(value.chest);
    };

    const applyLookAngles = (yaw: NumberBinding, pitch: NumberBinding) => (value: SetupModeValueDto) => {
        if (!value.lookAngles) return;
        // The inputs step by 0.1, and the browser rejects a raw float like -90.00361.
        const toInputStep = (angle: number) => Math.round(angle * 10) / 10 || 0;
        // Client yaw accumulates whole turns (-990 after spinning), so wrap it to -180..180.
        const wrappedYaw = ((value.lookAngles.yaw % 360) + 540) % 360 - 180;
        yaw.write(toInputStep(wrappedYaw));
        pitch.write(toInputStep(value.lookAngles.pitch));
    };

    const numField = (label: string, binding: NumberBinding, opts: { step?: string; placeholder?: string; unit?: string } = {}) => (
        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <span class={styles.fieldLabel}>
                <Text val={label} />
                <Show when={opts.unit}><span class={styles.helper} style={{ 'margin-left': '6px' }}>{opts.unit}</span></Show>
            </span>
            <input type="number" step={opts.step ?? '1'} placeholder={opts.placeholder}
                value={binding.read()}
                onInput={(e) => binding.write(Number(e.currentTarget.value))} />
        </label>
    );

    const coordField = (coords: CoordBinding, opts: { fractional?: boolean } = {}) => (
        <CoordinateInput value={coords.read()} unit={blocksUnit()} fractional={opts.fractional}
            onChange={(next) => coords.write(next)} />
    );

    const areaField = (labelKey: string, fieldId: string, cornerA: CoordBinding, cornerB: CoordBinding) => (
        <AreaInput
            corner1={cornerA.read()}
            corner2={cornerB.read()}
            onChange={(corner1, corner2) => { cornerA.write(corner1); cornerB.write(corner2); }}
            unit={blocksUnit()}
            fieldId={fieldId}
            label={t(labelKey)}
            allowedBots={botId() ? [botId()!] : []}
            anchor={homeAnchor()}
            reach={SELLBOT_REACH} />
    );

    return (
        <div class={styles.container}>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_SETTINGS_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_SETTINGS_SUBTITLE" /></span>
                </div>
            </div>

            <Show when={botId()} fallback={
                <div class={styles.empty}><i class="fa-solid fa-robot" style={{ 'font-size': '3rem', opacity: 0.5 }} /><p><Text val="SELLBOT_NO_BOT" /></p></div>
            }>
                <Show when={!loading()} fallback={<div class={styles.skeletonCard} />}>
                    <form class="fcard v" style={{ gap: 'var(--spacing-md)' }} onSubmit={(e) => { e.preventDefault(); void saveConfig(); }}>
                        <div class={styles.sectionHeader}>
                            <h2><Text val="SELLBOT_GENERAL" /></h2>
                            <span class={styles.helper}><Text val="SELLBOT_GENERAL_HELP" /></span>
                        </div>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_LOG_LEVEL" /></span>
                            <select value={form.defaultLogLevel} onChange={(e) => setForm('defaultLogLevel', e.currentTarget.value)}>
                                <For each={LOG_LEVELS}>{(l) => <option value={l}>{t('SELLBOT_LOG_' + l.toUpperCase())}</option>}</For>
                            </select>
                            <span class={styles.helper}><Text val="SELLBOT_LOG_LEVEL_HELP" /></span>
                        </label>

                        <Show when={isFeatureEnabled(MULTI_ZONE_FLAG) || isMultiZone()}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="SELLBOT_ZONE_MODE" /></span>
                                <select value={form.zoneMode} onChange={(e) => changeZoneMode(e.currentTarget.value)}>
                                    <For each={ZONE_MODES}>{(zoneMode) => <option value={zoneMode}>{t('SELLBOT_ZONE_MODE_' + zoneMode.toUpperCase())}</option>}</For>
                                </select>
                                <span class={styles.helper}><Text val="SELLBOT_ZONE_MODE_HELP" /></span>
                            </label>
                        </Show>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="checkbox" checked={form.autoEnablePlotAcl}
                                onChange={(e) => setForm('autoEnablePlotAcl', e.currentTarget.checked)} />
                            <span class={styles.fieldLabel}><Text val="SELLBOT_AUTO_ENABLE_PLOTACL" /></span>
                        </label>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="checkbox" checked={form.scanChamberOnEnable}
                                onChange={(e) => setForm('scanChamberOnEnable', e.currentTarget.checked)} />
                            <span class={styles.fieldLabel}><Text val="SELLBOT_SCAN_CHAMBER_ON_ENABLE" /></span>
                        </label>


                        <div class={styles.sectionHeader}>
                            <h2><Text val="SELLBOT_HOME" /></h2>
                            <span class={styles.helper}><Text val="SELLBOT_HOME_HELP" /></span>
                        </div>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}>
                                <Text val="SELLBOT_HOME_NAME" />
                                <span class={styles.fieldRequired}>*</span>
                            </span>
                            <input type="text" placeholder="home" maxlength="32"
                                value={form.homeName} onInput={(e) => setForm('homeName', e.currentTarget.value)} />
                            <span class={styles.helper}><Text val="SELLBOT_HOME_NAME_HELP" /></span>
                        </label>

                        {coordField(formCoords('homeX', 'homeY', 'homeZ'), { fractional: true })}
                        <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                            <span class={styles.helper}><Text val="SELLBOT_HOME_COORDS_HELP" /></span>
                            <button type="button" class="btn btn-sm btn-secondary" disabled={capturing() || !form.homeName?.trim()} onClick={() => void captureHome()}>
                                <Show when={capturing()} fallback={<><i class="fa-solid fa-location-crosshairs" /> <Text val="SELLBOT_CAPTURE_HOME" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SELLBOT_CAPTURING_SHORT" />
                                </Show>
                            </button>
                        </div>

                        <div class={styles.sectionHeader}>
                            <h2><Text val="SELLBOT_DROP_DIRECTION" /></h2>
                            <span class={styles.helper}>
                                <Show when={isMultiZone()} fallback={<Text val="SELLBOT_DROP_DIRECTION_HELP" />}>
                                    <Text val="SELLBOT_DROP_SETTLE_DELAY_ZONES_HELP" />
                                </Show>
                            </span>
                        </div>
                        <div class={styles.coordRow}>
                            <Show when={!isMultiZone()}>
                                {numField('SELLBOT_YAW', formNumber('dropYaw'), { step: '0.1', unit: '°', placeholder: '0' })}
                                {numField('SELLBOT_PITCH', formNumber('dropPitch'), { step: '0.1', unit: '°', placeholder: '0' })}
                            </Show>
                            {numField('SELLBOT_DROP_SETTLE_DELAY', formNumber('dropSettleDelayMs'), { step: '1', unit: 'ms', placeholder: '150' })}
                        </div>
                        <Show when={!isMultiZone()}>
                            {pickLabel('SELLBOT_DROP_DIRECTION', 'sellbot.dropAim', 'LookAngles',
                                lookValue(formNumber('dropYaw'), formNumber('dropPitch')),
                                applyLookAngles(formNumber('dropYaw'), formNumber('dropPitch')))}

                            <div class={styles.sectionHeader}>
                                <h2><Text val="SELLBOT_SELL_AREA" /></h2>
                                <span class={styles.helper}><Text val="SELLBOT_SELL_AREA_HELP" /></span>
                            </div>
                            {areaField('SELLBOT_SELL_AREA', 'sellbot.sellArea',
                                formCoords('sellAreaMinX', 'sellAreaMinY', 'sellAreaMinZ'), formCoords('sellAreaMaxX', 'sellAreaMaxY', 'sellAreaMaxZ'))}
                        </Show>

                        <Show when={isMultiZone()}>
                            <div class={styles.sectionHeader}>
                                <h2><Text val="SELLBOT_ZONES" /></h2>
                                <span class={styles.helper}><Text val="SELLBOT_ZONES_HELP" /></span>
                            </div>

                            <For each={zoneMergeConflicts()}>{(conflict) => (
                                <div class={styles.warningCallout}>
                                    <i class="fa-solid fa-triangle-exclamation" />
                                    <span>{t('SELLBOT_ZONE_MERGE_CONFLICT', mergeConflictZoneName(conflict.firstZoneId), mergeConflictZoneName(conflict.secondZoneId))}</span>
                                </div>
                            )}</For>
                            <Show when={zonesWithoutDropPath().length > 0}>
                                <div class={styles.warningCallout}>
                                    <i class="fa-solid fa-route" />
                                    <span>{t('SELLBOT_ZONE_DROP_PATH_MISSING', zonesWithoutDropPath().join(', '))}</span>
                                </div>
                            </Show>

                            <Show when={form.zones.length === 0}>
                                <div class={styles.empty} style={{ padding: 'var(--spacing-md)' }}>
                                    <i class="fa-solid fa-draw-polygon" style={{ 'font-size': '2rem', opacity: 0.4 }} />
                                    <p><Text val="SELLBOT_ZONES_EMPTY" /></p>
                                </div>
                            </Show>
                            <div class={styles.zoneList}>
                                <Index each={form.zones}>
                                    {(zone, zoneIndex) => {
                                        const sellAreaCornerA = zoneCoords(zoneIndex, 'sellAreaMinX', 'sellAreaMinY', 'sellAreaMinZ');
                                        const sellAreaCornerB = zoneCoords(zoneIndex, 'sellAreaMaxX', 'sellAreaMaxY', 'sellAreaMaxZ');
                                        const dropYaw = zoneNumber(zoneIndex, 'dropYaw');
                                        const dropPitch = zoneNumber(zoneIndex, 'dropPitch');
                                        const ankaufChest = zoneCoords(zoneIndex, 'ankaufChestX', 'ankaufChestY', 'ankaufChestZ');
                                        const ankaufLamp = zoneCoords(zoneIndex, 'lampX', 'lampY', 'lampZ');
                                        const quoteLamp = zoneCoords(zoneIndex, 'quoteLampX', 'quoteLampY', 'quoteLampZ');
                                        const recordedZone = () => savedZone(zone().id);
                                        const recordBlocked = () => !zone().id || formDirty();
                                        return (
                                            <div class={styles.zoneCard}>
                                                <div class={styles.zoneHeader}>
                                                    <label class="v" style={{ gap: 'var(--spacing-xs)', flex: '1' }}>
                                                        <span class={styles.fieldLabel}>
                                                            <Text val="SELLBOT_ZONE_NAME" />
                                                            <span class={styles.fieldRequired}>*</span>
                                                        </span>
                                                        <input type="text" maxlength="64" value={zone().name}
                                                            onInput={(e) => setForm('zones', zoneIndex, 'name', e.currentTarget.value)} />
                                                    </label>
                                                    <button type="button" class="btn btn-danger btn-sm btn-icon" title={t('SELLBOT_ZONE_REMOVE')} onClick={() => void removeZone(zoneIndex)}>
                                                        <i class="fa-solid fa-trash" />
                                                    </button>
                                                </div>

                                                {areaField('SELLBOT_SELL_AREA', `sellbot.zone.${zoneIndex}.area`, sellAreaCornerA, sellAreaCornerB)}

                                                {pickLabel('SELLBOT_DROP_DIRECTION', `sellbot.zone.${zoneIndex}.dropAim`, 'LookAngles',
                                                    lookValue(dropYaw, dropPitch), applyLookAngles(dropYaw, dropPitch))}
                                                <div class={styles.coordRow}>
                                                    {numField('SELLBOT_YAW', dropYaw, { step: '0.1', unit: '°', placeholder: '0' })}
                                                    {numField('SELLBOT_PITCH', dropPitch, { step: '0.1', unit: '°', placeholder: '0' })}
                                                </div>

                                                {pickLabel('SELLBOT_ANKAUF_CHEST', `sellbot.zone.${zoneIndex}.ankaufChest`, 'ChestRef',
                                                    chestValue(ankaufChest), applyChest(ankaufChest))}
                                                {coordField(ankaufChest)}

                                                {pickLabel('SELLBOT_ANKAUF_LAMP', `sellbot.zone.${zoneIndex}.ankaufLamp`, 'BlockPos',
                                                    blockValue(ankaufLamp), applyBlock(ankaufLamp))}
                                                {coordField(ankaufLamp)}

                                                {pickLabel('SELLBOT_QUOTE_LAMP', `sellbot.zone.${zoneIndex}.quoteLamp`, 'BlockPos',
                                                    blockValue(quoteLamp), applyBlock(quoteLamp))}
                                                {coordField(quoteLamp)}

                                                <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                                        <span class={styles.fieldLabel}><Text val="SELLBOT_ZONE_DROP_PATH" /></span>
                                                        <Show when={recordedZone()?.dropPathRecordedAt} fallback={<span><Text val="SELLBOT_ZONE_DROP_PATH_NONE" /></span>}>
                                                            {(recordedAt) => <span>{t('SELLBOT_ZONE_DROP_PATH_RECORDED', recordedZone()!.dropPathCount, formatDateTime(recordedAt()))}</span>}
                                                        </Show>
                                                    </div>
                                                    <button type="button" class="btn btn-sm btn-secondary"
                                                        disabled={recordBlocked() || recordingZoneId() !== null}
                                                        onClick={() => void recordDropPath(zone().id!)}>
                                                        <Show when={recordingZoneId() !== null && recordingZoneId() === zone().id}
                                                            fallback={<><i class="fa-solid fa-route" /> <Text val="SELLBOT_ZONE_RECORD_DROP_PATH" /></>}>
                                                            <i class="fa-solid fa-spinner fa-spin" /> <Text val="SELLBOT_ZONE_RECORDING_DROP_PATH" />
                                                        </Show>
                                                    </button>
                                                </div>
                                                <span class={styles.helper}><Text val="SELLBOT_ZONE_DROP_PATH_HELP" /></span>
                                                <Show when={recordBlocked()}>
                                                    <span class={styles.warning}><Text val="SELLBOT_ZONE_DROP_PATH_SAVE_FIRST" /></span>
                                                </Show>
                                            </div>
                                        );
                                    }}
                                </Index>
                            </div>
                            <div>
                                <button type="button" class="btn btn-secondary btn-sm" onClick={addZone}>
                                    <i class="fa-solid fa-plus" /> <Text val="SELLBOT_ZONE_ADD" />
                                </button>
                            </div>
                        </Show>

                        <div class={styles.sectionHeader}>
                            <h2><Text val="SELLBOT_CHAMBER_AREA" /></h2>
                            <span class={styles.helper}><Text val="SELLBOT_CHAMBER_AREA_HELP" /></span>
                        </div>
                        {areaField('SELLBOT_CHAMBER_AREA', 'sellbot.chamberArea',
                            formCoords('chamberAreaMinX', 'chamberAreaMinY', 'chamberAreaMinZ'), formCoords('chamberAreaMaxX', 'chamberAreaMaxY', 'chamberAreaMaxZ'))}

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_CHAMBER_INTRUDER_ACTION" /></span>
                            <select value={form.chamberIntruderAction} onChange={(e) => setForm('chamberIntruderAction', e.currentTarget.value)}>
                                <For each={PLOT_ACL_ACTIONS}>{(a) => <option value={a}>{t('PLOTACL_ACTION_' + a)}</option>}</For>
                            </select>
                            <span class={styles.helper}><Text val="SELLBOT_CHAMBER_INTRUDER_ACTION_HELP" /></span>
                        </label>

                        <div class={styles.sectionHeader} style={{ 'margin-top': 'var(--spacing-md)' }}>
                            <h2><Text val="SELLBOT_CHAMBER" /></h2>
                            <span class={styles.helper}><Text val="SELLBOT_CHAMBER_HELP" /></span>
                        </div>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_CHAMBER_STORAGE" /></span>
                            <StorageSelect
                                storages={chamberChoices()}
                                value={form.sellChamberStorageId}
                                onChange={(storageId) => setForm('sellChamberStorageId', storageId)}
                                emptyOption={{ label: t('SELLBOT_CHAMBER_STORAGE_NEW'), icon: 'fa-plus' }}
                            />
                            <Show when={savedChamber() && savedChamber()!.name == null} fallback={
                                <span class={styles.helper}><Text val="SELLBOT_CHAMBER_STORAGE_HELP" /></span>
                            }>
                                <span class={styles.warning}><Text val="SELLBOT_CHAMBER_STORAGE_MISSING" /></span>
                            </Show>
                        </label>
                        <Show when={chamber()?.chestCount} fallback={
                            <div class={styles.empty} style={{ padding: 'var(--spacing-md)' }}>
                                <i class="fa-solid fa-box-open" style={{ 'font-size': '2rem', opacity: 0.4 }} />
                                <p><Text val="SELLBOT_CHAMBER_EMPTY" /></p>
                            </div>
                        }>
                            <div class="h" style={{ gap: 'var(--spacing-xl)', 'flex-wrap': 'wrap' }}>
                                <div class="v" style={{ gap: '2px' }}>
                                    <span class={styles.fieldLabel}><Text val="SELLBOT_CHAMBER_CHESTS" /></span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-lg)' }}>{chamber()!.chestCount}</span>
                                </div>
                                <Show when={chamber()!.bounds}>
                                    <div class="v" style={{ gap: '2px' }}>
                                        <span class={styles.fieldLabel}><Text val="SELLBOT_CHAMBER_BOUNDS" /></span>
                                        <span style={{ 'font-family': 'var(--font-family-mono)' }}>
                                            ({chamber()!.bounds!.minX}, {chamber()!.bounds!.minY}, {chamber()!.bounds!.minZ}) → ({chamber()!.bounds!.maxX}, {chamber()!.bounds!.maxY}, {chamber()!.bounds!.maxZ})
                                        </span>
                                    </div>
                                </Show>
                                <Show when={chamber()!.lastScannedAt}>
                                    <div class="v" style={{ gap: '2px' }}>
                                        <span class={styles.fieldLabel}><Text val="SELLBOT_CHAMBER_LAST_SCAN" /></span>
                                        <span>{formatDateTime(chamber()!.lastScannedAt!)}</span>
                                    </div>
                                </Show>
                            </div>
                        </Show>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'margin-top': 'var(--spacing-md)' }}>
                            <input type="checkbox" checked={form.allowCompressedChamberRetrieval}
                                onChange={(e) => setForm('allowCompressedChamberRetrieval', e.currentTarget.checked)} />
                            <span class={styles.fieldLabel}><Text val="SELLBOT_ALLOW_COMPRESSED_RETRIEVAL" /></span>
                        </label>
                        <span class={styles.helper}><Text val="SELLBOT_ALLOW_COMPRESSED_RETRIEVAL_HELP" /></span>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'margin-top': 'var(--spacing-md)' }}>
                            <input type="checkbox" checked={form.listAsCompressed}
                                onChange={(e) => setForm('listAsCompressed', e.currentTarget.checked)} />
                            <span class={styles.fieldLabel}><Text val="SELLBOT_LIST_AS_COMPRESSED" /></span>
                        </label>
                        <span class={styles.helper}><Text val="SELLBOT_LIST_AS_COMPRESSED_HELP" /></span>

                        <div class={styles.sectionHeader} style={{ 'margin-top': 'var(--spacing-md)' }}>
                            <h2><Text val="SELLBOT_ANKAUF" /></h2>
                            <span class={styles.helper}><Text val="SELLBOT_ANKAUF_HELP" /></span>
                        </div>

                        <Show when={!isMultiZone()} fallback={<span class={styles.helper}><Text val="SELLBOT_ZONE_SETUP_PER_ZONE" /></span>}>
                            {pickLabel('SELLBOT_ANKAUF_CHEST', 'sellbot.ankaufChest', 'ChestRef',
                                chestValue(formCoords('ankaufChestX', 'ankaufChestY', 'ankaufChestZ')),
                                applyChest(formCoords('ankaufChestX', 'ankaufChestY', 'ankaufChestZ')))}
                            {coordField(formCoords('ankaufChestX', 'ankaufChestY', 'ankaufChestZ'))}
                        </Show>

                        {pickLabel('SELLBOT_VERIFICATION_CHEST', 'sellbot.verificationChest', 'ChestRef',
                            chestValue(formCoords('verificationChestX', 'verificationChestY', 'verificationChestZ')),
                            applyChest(formCoords('verificationChestX', 'verificationChestY', 'verificationChestZ')))}
                        {coordField(formCoords('verificationChestX', 'verificationChestY', 'verificationChestZ'))}
                        <span class={styles.helper}><Text val="SELLBOT_VERIFICATION_CHEST_HELP" /></span>

                        {pickLabel('SELLBOT_TRASH_CHEST', 'sellbot.trashChest', 'ChestRef',
                            chestValue(formCoords('trashChestX', 'trashChestY', 'trashChestZ')),
                            applyChest(formCoords('trashChestX', 'trashChestY', 'trashChestZ')))}
                        {coordField(formCoords('trashChestX', 'trashChestY', 'trashChestZ'))}
                        <span class={styles.helper}><Text val="SELLBOT_TRASH_CHEST_HELP" /></span>

                        {pickLabel('SELLBOT_LAGER_CHEST', 'sellbot.lagerChest', 'ChestRef',
                            chestValue(formCoords('lagerChestX', 'lagerChestY', 'lagerChestZ')),
                            applyChest(formCoords('lagerChestX', 'lagerChestY', 'lagerChestZ')))}
                        {coordField(formCoords('lagerChestX', 'lagerChestY', 'lagerChestZ'))}
                        <span class={styles.helper}><Text val="SELLBOT_LAGER_CHEST_HELP" /></span>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_ANKAUF_DEFAULT_DESTINATION" /></span>
                            <select value={form.ankaufDefaultDestination} onChange={(e) => setForm('ankaufDefaultDestination', e.currentTarget.value)}>
                                <For each={ANKAUF_DESTINATIONS}>{(d) => <option value={d}>{t('SELLBOT_DESTINATION_' + d.toUpperCase())}</option>}</For>
                            </select>
                            <span class={styles.helper}><Text val="SELLBOT_ANKAUF_DEFAULT_DESTINATION_HELP" /></span>
                        </label>

                        <Show when={!isMultiZone()}>
                            {pickLabel('SELLBOT_ANKAUF_LAMP', 'sellbot.ankaufLamp', 'BlockPos',
                                blockValue(formCoords('lampX', 'lampY', 'lampZ')),
                                applyBlock(formCoords('lampX', 'lampY', 'lampZ')))}
                            {coordField(formCoords('lampX', 'lampY', 'lampZ'))}
                            <span class={styles.helper}><Text val="SELLBOT_ANKAUF_LAMP_HELP" /></span>
                        </Show>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_ANKAUF_TRIGGER" /></span>
                            <select value={form.ankaufTrigger} onChange={(e) => setForm('ankaufTrigger', e.currentTarget.value)}>
                                <For each={ANKAUF_TRIGGERS}>{(tr) => <option value={tr}>{t('SELLBOT_ANKAUF_TRIGGER_' + tr.toUpperCase())}</option>}</For>
                            </select>
                            <span class={styles.helper}><Text val="SELLBOT_ANKAUF_TRIGGER_HELP" /></span>
                        </label>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="checkbox" checked={form.ankaufMsgTriggerEnabled}
                                onChange={(e) => setForm('ankaufMsgTriggerEnabled', e.currentTarget.checked)} />
                            <span class={styles.fieldLabel}><Text val="SELLBOT_ANKAUF_MSG_TRIGGER_ENABLED" /></span>
                        </label>
                        <span class={styles.helper}><Text val="SELLBOT_ANKAUF_MSG_TRIGGER_ENABLED_HELP" /></span>

                        <Show when={form.ankaufMsgTriggerEnabled}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}>
                                    <Text val="SELLBOT_ANKAUF_MSG_TRIGGER_WORD" />
                                    <span class={styles.fieldRequired}>*</span>
                                </span>
                                <input type="text" placeholder="ankauf" maxlength="32"
                                    value={form.ankaufMsgTriggerWord}
                                    onInput={(e) => setForm('ankaufMsgTriggerWord', e.currentTarget.value)} />
                                <span class={styles.helper}><Text val="SELLBOT_ANKAUF_MSG_TRIGGER_WORD_HELP" /></span>
                            </label>
                        </Show>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="checkbox" checked={form.ankaufAcceptsCompressed}
                                onChange={(e) => setForm('ankaufAcceptsCompressed', e.currentTarget.checked)} />
                            <span class={styles.fieldLabel}><Text val="SELLBOT_ANKAUF_ACCEPT_COMPRESSED" /></span>
                        </label>
                        <span class={styles.helper}><Text val="SELLBOT_ANKAUF_ACCEPT_COMPRESSED_HELP" /></span>

                        <div class={styles.sectionHeader} style={{ 'margin-top': 'var(--spacing-md)' }}>
                            <h2><Text val="SELLBOT_QUOTE" /></h2>
                            <span class={styles.helper}><Text val="SELLBOT_QUOTE_HELP" /></span>
                        </div>

                        <Show when={!isMultiZone()} fallback={<span class={styles.helper}><Text val="SELLBOT_ZONE_SETUP_PER_ZONE" /></span>}>
                            {pickLabel('SELLBOT_QUOTE_LAMP', 'sellbot.quoteLamp', 'BlockPos',
                                blockValue(formCoords('quoteLampX', 'quoteLampY', 'quoteLampZ')),
                                applyBlock(formCoords('quoteLampX', 'quoteLampY', 'quoteLampZ')))}
                            {coordField(formCoords('quoteLampX', 'quoteLampY', 'quoteLampZ'))}
                            <span class={styles.helper}><Text val="SELLBOT_QUOTE_LAMP_HELP" /></span>
                        </Show>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_QUOTE_TRIGGER" /></span>
                            <select value={form.quoteTrigger} onChange={(e) => setForm('quoteTrigger', e.currentTarget.value)}>
                                <For each={ANKAUF_TRIGGERS}>{(tr) => <option value={tr}>{t('SELLBOT_ANKAUF_TRIGGER_' + tr.toUpperCase())}</option>}</For>
                            </select>
                            <span class={styles.helper}><Text val="SELLBOT_QUOTE_TRIGGER_HELP" /></span>
                        </label>

                        <div class="h" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)', 'margin-top': 'var(--spacing-md)' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => void rescan()}>
                                <i class="fa-solid fa-magnifying-glass-arrow-right" /> <Text val="SELLBOT_RESCAN" />
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>
            </Show>
        </div>
    );
}
